import React from "react";
import { Box, IconButton, Tooltip, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import ReplayIcon from "@mui/icons-material/Replay";
import HistoryIcon from "@mui/icons-material/History";
import InstallDesktopIcon from "@mui/icons-material/InstallDesktop";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { format } from "date-fns";

// Helper functions to reduce repetition
const defaultValueGetter = (value) => value || "-";
const nullishValueGetter = (value) => value ?? "-";

const createActionButton = ({
  icon: Icon,
  tooltip,
  color,
  disabled,
  onClick,
}) => (
  <Tooltip title={tooltip}>
    <span>
      <IconButton
        size="medium"
        color={color}
        disabled={disabled}
        onClick={onClick}
      >
        <Icon />
      </IconButton>
    </span>
  </Tooltip>
);

const handleActionClick = (e, callback) => {
  e.stopPropagation();
  e.preventDefault();
  callback();
};

const createBasicColumn = (field, headerName, options = {}) => ({
  field,
  headerName,
  minWidth: 120,
  flex: 1,
  valueGetter: defaultValueGetter,
  ...options,
});

const renderStatusCell = ({ row }) => {
  if (row.retired) {
    return (
      <Chip label="Retired" color="error" variant="outlined" size="small" />
    );
  }
  if (row.well_ndx) {
    return (
      <Chip
        label={`Active on ${row.well_name}`}
        color="success"
        size="medium"
      />
    );
  }
  return (
    <Chip label="Available" color="info" variant="outlined" size="small" />
  );
};

const createActionsColumn = (handlers) => ({
  field: "actions",
  headerName: "Actions",
  width: 250,
  sortable: false,
  renderCell: ({ row }) => (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      {createActionButton({
        icon: EditIcon,
        tooltip: "Edit",
        onClick: (e) =>
          handleActionClick(e, () => {
            handlers.setSelectedMeterId(row.meter_ndx);
            handlers.handleEditMeter(row);
          }),
      })}

      {createActionButton({
        icon: InstallDesktopIcon,
        tooltip: row.well_ndx ? "Already installed" : "Install",
        color: "success",
        disabled: !!row.well_ndx,
        onClick: (e) =>
          handleActionClick(e, () => {
            handlers.setSelectedMeterId(row.meter_ndx);
            handlers.handleInstall(row);
          }),
      })}

      {createActionButton({
        icon: SwapHorizIcon,
        tooltip: !row.well_ndx ? "Not installed" : "Transfer",
        color: "info",
        disabled: !row.well_ndx,
        onClick: (e) =>
          handleActionClick(e, () => {
            handlers.setSelectedMeterId(row.meter_ndx);
            handlers.handleTransfer(row);
          }),
      })}

      {createActionButton({
        icon: RemoveCircleIcon,
        tooltip: !row.well_ndx ? "Not installed" : "Remove",
        color: "warning",
        disabled: !row.well_ndx,
        onClick: (e) =>
          handleActionClick(e, () => {
            handlers.setSelectedMeterId(row.meter_ndx);
            handlers.handleRemove(row);
          }),
      })}

      {createActionButton({
        icon: HistoryIcon,
        tooltip: "Installation History",
        onClick: (e) =>
          handleActionClick(e, () => {
            handlers.setSelectedMeterId(row.meter_ndx);
            handlers.handleHistory(row.meter_ndx);
          }),
      })}

      {createActionButton({
        icon: row.retired ? ReplayIcon : BlockIcon,
        tooltip: row.retired
          ? "Reactivate meter"
          : row.well_ndx
          ? "Cannot retire installed meter"
          : "Retire meter",
        color: row.retired ? "success" : "error",
        disabled: !!row.well_ndx && !row.retired,
        onClick: (e) =>
          handleActionClick(e, () => {
            handlers.setSelectedMeterId(row.meter_ndx);
            handlers.handleDeleteRequest(row.meter_ndx);
          }),
      })}
    </Box>
  ),
});

export const createColumns = (handlers) => [
  createActionsColumn(handlers),

  {
    field: "status",
    headerName: "Status",
    minWidth: 250,
    flex: 1,
    renderCell: renderStatusCell,
  },

  createBasicColumn("meter_id", "Meter ID"),
  createBasicColumn("meter_serial", "Serial Number", { minWidth: 150 }),
  createBasicColumn("meter_make", "Make"),
  createBasicColumn("meter_model", "Model"),
  createBasicColumn("meter_type", "Type", { minWidth: 100 }),
  createBasicColumn("unit_native", "Unit Native", { minWidth: 100 }),
  createBasicColumn("gal_conversion", "Gal Conversion"),

  createBasicColumn("rollover_num", "Rollover", {
    minWidth: 100,
    valueGetter: nullishValueGetter,
  }),

  createBasicColumn("well_name", "Well", {
    minWidth: 150,
    valueGetter: (value, row) => row.well_name || "-",
  }),

  {
    field: "current_installed_at",
    headerName: "Installed Date",
    width: 150,
    valueGetter: (value) => {
      if (!value) return "Not installed";
      return format(new Date(value), "MMM dd, yyyy");
    },
  },

  createBasicColumn("notes", "Notes", {
    minWidth: 200,
    flex: 2,
  }),
];
